import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { formatRelativeEta, formatTime } from '@lib/datetime';
import { ROUTES } from '@lib/routes';
import type { StopRow } from './driverStopsModel';

const DASH = '—';
const ROW_CLASS =
  'flex min-h-11 items-center gap-2.5 border-t border-border/50 px-3.5 py-2.5 text-foreground no-underline';

const jobPath = (jobId: string) => `${ROUTES.dispatch.jobs}/${encodeURIComponent(jobId)}`;

/** One driver-task row: still to deliver (ETA) or delivered (completion time). */
export function StopLine({ row, nowMs }: { row: StopRow; nowMs: number }) {
  const { t } = useTranslation();
  const jobId = row.jobIds[0];
  const label =
    row.orderNames.length > 0 ? row.orderNames.join(' · ') : (row.fallbackOrderName ?? DASH);
  const delivered = row.completedAt !== null;
  const relative = row.etaMs === null ? null : formatRelativeEta(row.etaMs, new Date(nowMs));

  const content = (
    <>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{label}</span>
        {row.jobIds.length >= 2 && (
          <span className="text-[12.5px] text-muted-foreground">
            {t('dispatch.phone.stops.sameAddress', { count: row.jobIds.length })}
          </span>
        )}
        {row.active && (
          <span className="text-[12.5px] text-muted-foreground">
            {t('dispatch.eta.state.active')}
          </span>
        )}
      </span>
      {delivered ? (
        <span className="font-mono text-[12.5px] text-muted-foreground">
          {formatTime(row.completedAt)}
        </span>
      ) : row.etaMs === null ? (
        <span className="font-mono text-[12.5px] text-muted-foreground">{DASH}</span>
      ) : (
        <span className="flex flex-col items-end">
          <span
            data-late={row.late ? 'true' : undefined}
            className={`font-mono text-[12.5px] ${row.late ? 'font-medium text-warning' : ''}`}
          >
            {formatTime(row.etaMs)}
          </span>
          {relative && <span className="text-[12px] text-muted-foreground">{relative.text}</span>}
        </span>
      )}
    </>
  );

  if (jobId === undefined) {
    return <div className={ROW_CLASS}>{content}</div>;
  }
  return (
    <Link to={jobPath(jobId)} className={ROW_CLASS}>
      {content}
      <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}
